"use client";

import type { CSSProperties, FC, Ref } from "react";

import { Carousel } from "@huub-web/ui";

import { TemplatesParser } from "../../components/TemplatesParser/TemplatesParser";
import type { TemplateCollection } from "../../types/TemplateCollection";
import {
  carouselWrapperStyle,
  gridStyle,
  gridWrapperStyle,
  rootStyle,
} from "./CardOverviewGrid.css";

function getColumns({ count }: { count: number }): number {
  if (count <= 3) return count;
  if (count === 4) return 2;
  if (count <= 6) return 3;
  return Math.ceil(count / Math.ceil(count / 3));
}

function getColumnsLg({ count }: { count: number }): number {
  if (count <= 4) return count;
  if (count <= 6) return 3;
  return Math.ceil(count / Math.ceil(count / 4));
}

export type CardOverviewGridTemplateProps = {
  /**
   * The collection of hub card templates to render.
   */
  templates: TemplateCollection;
  /**
   * Ref forwarded to the root element.
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * Renders hub cards in a responsive CSS grid on tablet+ and a carousel on
 * mobile. Owns its own layout — no outer Container wrapping needed.
 */
export const CardOverviewGridTemplate: FC<CardOverviewGridTemplateProps> = ({
  templates,
  ref,
}) => {
  const columns = getColumns({ count: templates.length });
  const columnsLg = getColumnsLg({ count: templates.length });

  return (
    <div ref={ref} className={rootStyle}>
      {/* Mobile: horizontal scroll carousel */}
      <div className={carouselWrapperStyle}>
        <Carousel variant="default" align="stretch">
          <TemplatesParser templates={templates} level={1} />
        </Carousel>
      </div>

      {/* Tablet+: CSS grid */}
      <div className={gridWrapperStyle}>
        <div
          className={gridStyle}
          style={
            {
              "--columns": String(columns),
              "--columns-lg": String(columnsLg),
            } as CSSProperties
          }
        >
          <TemplatesParser templates={templates} level={1} />
        </div>
      </div>
    </div>
  );
};
